import React, { useState } from 'react';
import { Plus, Search, Edit, Trash, Save, X, Eye, ArrowUp, ArrowDown, Globe, Check, FileText, AlertCircle, Book, Users } from 'lucide-react';
import { Card } from '../ui/Card';
import { Table, Column } from '../ui/Table';
import { TablePagination } from '../ui/TablePagination';
import { Badge } from '../ui/Badge';
import { useLanguage } from '../../contexts/LanguageContext';
interface TermsSection {
  id: string;
  title: string;
  content: string;
  order: number;
  lastUpdated: string;
  status: 'published' | 'draft';
  language?: 'en' | 'ar';
  category: 'usage' | 'accounts' | 'intellectual' | 'termination';
}
export const TermsOfServicePage = () => {
  const {
    language
  } = useLanguage();
  const [sections, setSections] = useState<TermsSection[]>([{
    id: '1',
    title: 'Platform Usage',
    content: 'By using our platform, you agree to comply with all applicable laws and use the service responsibly.',
    order: 1,
    lastUpdated: '2024-05-15',
    status: 'published',
    language: 'en',
    category: 'usage'
  }, {
    id: '2',
    title: 'User Accounts',
    content: 'You are responsible for maintaining the confidentiality of your account information and all activities that occur under your account.',
    order: 2,
    lastUpdated: '2024-05-15',
    status: 'published',
    language: 'en',
    category: 'accounts'
  }, {
    id: '3',
    title: 'Intellectual Property',
    content: 'All content on the platform is protected by copyright and intellectual property rights.',
    order: 3,
    lastUpdated: '2024-05-15',
    status: 'published',
    language: 'en',
    category: 'intellectual'
  }, {
    id: '4',
    title: 'Service Termination',
    content: 'We reserve the right to terminate or suspend your account in case of violation of these terms.',
    order: 4,
    lastUpdated: '2024-05-15',
    status: 'published',
    language: 'en',
    category: 'termination'
  }, {
    id: '5',
    title: 'استخدام المنصة',
    content: 'باستخدام منصتنا، أنت توافق على الامتثال لجميع القوانين المعمول بها واستخدام الخدمة بشكل مسؤول.',
    order: 1,
    lastUpdated: '2024-05-15',
    status: 'published',
    language: 'ar',
    category: 'usage'
  }, {
    id: '6',
    title: 'حسابات المستخدمين',
    content: 'أنت مسؤول عن الحفاظ على سرية معلومات حسابك وجميع الأنشطة التي تحدث تحت حسابك.',
    order: 2,
    lastUpdated: '2024-05-15',
    status: 'published',
    language: 'ar',
    category: 'accounts'
  }]);
  const [editingSection, setEditingSection] = useState<TermsSection | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newSection, setNewSection] = useState<Omit<TermsSection, 'id'>>({
    title: '',
    content: '',
    order: sections.length + 1,
    lastUpdated: new Date().toISOString().split('T')[0],
    status: 'draft',
    language: language as 'en' | 'ar',
    category: 'usage'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>('order');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>('asc');
  const [languageFilter, setLanguageFilter] = useState<'all' | 'en' | 'ar'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'usage' | 'accounts' | 'intellectual' | 'termination'>('all');
  const itemsPerPage = 5;
  const filteredSections = sections.filter(section => {
    const matchesSearch = section.title.toLowerCase().includes(searchQuery.toLowerCase()) || section.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLanguage = languageFilter === 'all' || section.language === languageFilter;
    const matchesStatus = statusFilter === 'all' || section.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || section.category === categoryFilter;
    return matchesSearch && matchesLanguage && matchesStatus && matchesCategory;
  }).sort((a, b) => {
    if (sortColumn === 'order') {
      return sortDirection === 'asc' ? a.order - b.order : b.order - a.order;
    } else if (sortColumn === 'title') {
      return sortDirection === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
    } else if (sortColumn === 'lastUpdated') {
      return sortDirection === 'asc' ? new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime() : new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
    }
    return 0;
  });
  const paginatedSections = filteredSections.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredSections.length / itemsPerPage);
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === null) {
        setSortColumn(column);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const handleAddSection = () => {
    if (newSection.title.trim() === '' || newSection.content.trim() === '') {
      return;
    }
    const newId = (Math.max(...sections.map(s => parseInt(s.id))) + 1).toString();
    setSections([...sections, {
      id: newId,
      ...newSection
    }]);
    setNewSection({
      title: '',
      content: '',
      order: sections.length + 2,
      lastUpdated: new Date().toISOString().split('T')[0],
      status: 'draft',
      language: language as 'en' | 'ar',
      category: 'usage'
    });
    setIsAddingNew(false);
  };
  const handleUpdateSection = () => {
    if (!editingSection) return;
    setSections(sections.map(section => section.id === editingSection.id ? {
      ...editingSection,
      lastUpdated: new Date().toISOString().split('T')[0]
    } : section));
    setEditingSection(null);
  };
  const handleDeleteSection = (id: string) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      setSections(sections.filter(section => section.id !== id));
    }
  };
  const handleMoveUp = (id: string) => {
    const sectionIndex = sections.findIndex(s => s.id === id);
    if (sectionIndex <= 0) return;
    const updatedSections = [...sections];
    const currentOrder = updatedSections[sectionIndex].order;
    const prevOrder = updatedSections[sectionIndex - 1].order;
    updatedSections[sectionIndex].order = prevOrder;
    updatedSections[sectionIndex - 1].order = currentOrder;
    setSections(updatedSections);
  };
  const handleMoveDown = (id: string) => {
    const sectionIndex = sections.findIndex(s => s.id === id);
    if (sectionIndex >= sections.length - 1) return;
    const updatedSections = [...sections];
    const currentOrder = updatedSections[sectionIndex].order;
    const nextOrder = updatedSections[sectionIndex + 1].order;
    updatedSections[sectionIndex].order = nextOrder;
    updatedSections[sectionIndex + 1].order = currentOrder;
    setSections(updatedSections);
  };
  const handleToggleStatus = (id: string) => {
    setSections(sections.map(section => section.id === id ? {
      ...section,
      status: section.status === 'published' ? 'draft' : 'published',
      lastUpdated: new Date().toISOString().split('T')[0]
    } : section));
  };
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'usage':
        return <FileText className="w-4 h-4" data-id="element-1481" />;
      case 'accounts':
        return <Users className="w-4 h-4" data-id="element-1482" />;
      case 'intellectual':
        return <Book className="w-4 h-4" data-id="element-1483" />;
      case 'termination':
        return <AlertCircle className="w-4 h-4" data-id="element-1484" />;
      default:
        return <FileText className="w-4 h-4" data-id="element-1485" />;
    }
  };
  const getCategoryName = (category: string) => {
    switch (category) {
      case 'usage':
        return 'Platform Usage';
      case 'accounts':
        return 'User Accounts';
      case 'intellectual':
        return 'Intellectual Property';
      case 'termination':
        return 'Service Termination';
      default:
        return category;
    }
  };
  const columns: Column<TermsSection>[] = [{
    key: 'order',
    header: '#',
    sortable: true,
    render: section => section.order
  }, {
    key: 'title',
    header: 'Title',
    sortable: true,
    render: section => <div className="font-medium text-gray-900 dark:text-white" data-id="element-1486">
          {section.title}
        </div>
  }, {
    key: 'category',
    header: 'Category',
    sortable: true,
    render: section => <Badge variant="secondary" size="sm" className="flex items-center gap-1" data-id="element-1487">
          {getCategoryIcon(section.category)}
          <span data-id="element-1488">{getCategoryName(section.category)}</span>
        </Badge>
  }, {
    key: 'language',
    header: 'Language',
    sortable: true,
    render: section => <Badge variant={section.language === 'en' ? 'secondary' : 'warning'} size="sm" className="flex items-center gap-1" data-id="element-1489">
          <Globe className="w-3 h-3" data-id="element-1490" />
          <span data-id="element-1491">{section.language === 'en' ? 'English' : 'Arabic'}</span>
        </Badge>
  }, {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: section => <Badge variant={section.status === 'published' ? 'success' : 'secondary'} size="sm" data-id="element-1492">
          {section.status}
        </Badge>
  }, {
    key: 'lastUpdated',
    header: 'Last Updated',
    sortable: true,
    render: section => new Date(section.lastUpdated).toLocaleDateString()
  }, {
    key: 'actions',
    header: 'Actions',
    render: section => <div className="flex items-center gap-2" data-id="element-1493">
          <button onClick={() => handleMoveUp(section.id)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" title="Move Up" disabled={section.order === 1} data-id="element-1494">
            <ArrowUp className="w-4 h-4" data-id="element-1495" />
          </button>
          <button onClick={() => handleMoveDown(section.id)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" title="Move Down" disabled={section.order === sections.length} data-id="element-1496">
            <ArrowDown className="w-4 h-4" data-id="element-1497" />
          </button>
          <button onClick={() => handleToggleStatus(section.id)} className={`p-1 ${section.status === 'published' ? 'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300' : 'text-yellow-500 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300'}`} title={section.status === 'published' ? 'Change to Draft' : 'Publish Section'} data-id="element-1498">
            <Check className="w-4 h-4" data-id="element-1499" />
          </button>
          <button onClick={() => setEditingSection(section)} className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" title="Edit" data-id="element-1500">
            <Edit className="w-4 h-4" data-id="element-1501" />
          </button>
          <button onClick={() => handleDeleteSection(section.id)} className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" title="Delete" data-id="element-1502">
            <Trash className="w-4 h-4" data-id="element-1503" />
          </button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-1504">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1505">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1506">
          Terms of Service Management
        </h1>
        <button onClick={() => setIsAddingNew(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1507">
          <Plus className="w-4 h-4" data-id="element-1508" />
          Add Section
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1509">
        <Card className="p-4 flex items-center gap-3" data-id="element-1510">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30" data-id="element-1511">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1512" />
          </div>
          <div data-id="element-1513">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1514">
              Total Sections
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1515">
              {sections.length}
            </div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3" data-id="element-1516">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30" data-id="element-1517">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-1518" />
          </div>
          <div data-id="element-1519">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1520">
              Published
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1521">
              {sections.filter(section => section.status === 'published').length}
            </div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3" data-id="element-1522">
          <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30" data-id="element-1523">
            <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" data-id="element-1524" />
          </div>
          <div data-id="element-1525">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1526">
              English
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1527">
              {sections.filter(section => section.language === 'en').length}
            </div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3" data-id="element-1528">
          <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/30" data-id="element-1529">
            <Globe className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-1530" />
          </div>
          <div data-id="element-1531">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1532">
              Arabic
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1533">
              {sections.filter(section => section.language === 'ar').length}
            </div>
          </div>
        </Card>
      </div>
      <Card className="p-5" data-id="element-1534">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1535">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1536">
            Terms of Service Sections
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-1537">
            <div className="relative w-full sm:w-64" data-id="element-1538">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-1539" />
              <input type="text" placeholder="Search sections..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1540" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1541">
              <select value={languageFilter} onChange={e => setLanguageFilter(e.target.value as 'all' | 'en' | 'ar')} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1542">
                <option value="all" data-id="element-1543">All Languages</option>
                <option value="en" data-id="element-1544">English</option>
                <option value="ar" data-id="element-1545">Arabic</option>
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'all' | 'published' | 'draft')} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1546">
                <option value="all" data-id="element-1547">All Statuses</option>
                <option value="published" data-id="element-1548">Published</option>
                <option value="draft" data-id="element-1549">Draft</option>
              </select>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as 'all' | 'usage' | 'accounts' | 'intellectual' | 'termination')} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1550">
                <option value="all" data-id="element-1551">All Categories</option>
                <option value="usage" data-id="element-1552">Platform Usage</option>
                <option value="accounts" data-id="element-1553">User Accounts</option>
                <option value="intellectual" data-id="element-1554">Intellectual Property</option>
                <option value="termination" data-id="element-1555">Service Termination</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-hidden" data-id="element-1556">
          <div className="overflow-x-auto" data-id="element-1557">
            <Table data={paginatedSections} columns={columns} emptyMessage="No terms of service sections found" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} data-id="element-1558" />
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1559">
            <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-1560" />
          </div>
        </div>
      </Card>
      {isAddingNew && <Card className="p-5" data-id="element-1561">
          <div className="flex justify-between items-center mb-4" data-id="element-1562">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1563">
              Add New Section
            </h2>
            <button onClick={() => setIsAddingNew(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-1564">
              <X className="w-5 h-5" data-id="element-1565" />
            </button>
          </div>
          <div className="space-y-4" data-id="element-1566">
            <div data-id="element-1567">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1568">
                Title
              </label>
              <input type="text" value={newSection.title} onChange={e => setNewSection({
            ...newSection,
            title: e.target.value
          })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" placeholder="Enter section title" data-id="element-1569" />
            </div>
            <div data-id="element-1570">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1571">
                Content
              </label>
              <textarea value={newSection.content} onChange={e => setNewSection({
            ...newSection,
            content: e.target.value
          })} rows={4} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" placeholder="Enter section content" data-id="element-1572"></textarea>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-id="element-1573">
              <div data-id="element-1574">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1575">
                  Order
                </label>
                <input type="number" value={newSection.order} onChange={e => setNewSection({
              ...newSection,
              order: parseInt(e.target.value) || 0
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1576" />
              </div>
              <div data-id="element-1577">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1578">
                  Category
                </label>
                <select value={newSection.category} onChange={e => setNewSection({
              ...newSection,
              category: e.target.value as 'usage' | 'accounts' | 'intellectual' | 'termination'
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1579">
                  <option value="usage" data-id="element-1580">Platform Usage</option>
                  <option value="accounts" data-id="element-1581">User Accounts</option>
                  <option value="intellectual" data-id="element-1582">Intellectual Property</option>
                  <option value="termination" data-id="element-1583">Service Termination</option>
                </select>
              </div>
              <div data-id="element-1584">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1585">
                  Language
                </label>
                <select value={newSection.language} onChange={e => setNewSection({
              ...newSection,
              language: e.target.value as 'en' | 'ar'
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1586">
                  <option value="en" data-id="element-1587">English</option>
                  <option value="ar" data-id="element-1588">Arabic</option>
                </select>
              </div>
              <div data-id="element-1589">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1590">
                  Status
                </label>
                <select value={newSection.status} onChange={e => setNewSection({
              ...newSection,
              status: e.target.value as 'published' | 'draft'
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1591">
                  <option value="draft" data-id="element-1592">Draft</option>
                  <option value="published" data-id="element-1593">Published</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2" data-id="element-1594">
              <button onClick={() => setIsAddingNew(false)} className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1595">
                Cancel
              </button>
              <button onClick={handleAddSection} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2" data-id="element-1596">
                <Save className="w-4 h-4" data-id="element-1597" />
                Save Section
              </button>
            </div>
          </div>
        </Card>}
      {editingSection && <Card className="p-5" data-id="element-1598">
          <div className="flex justify-between items-center mb-4" data-id="element-1599">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1600">
              Edit Section
            </h2>
            <button onClick={() => setEditingSection(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-1601">
              <X className="w-5 h-5" data-id="element-1602" />
            </button>
          </div>
          <div className="space-y-4" data-id="element-1603">
            <div data-id="element-1604">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1605">
                Title
              </label>
              <input type="text" value={editingSection.title} onChange={e => setEditingSection({
            ...editingSection,
            title: e.target.value
          })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1606" />
            </div>
            <div data-id="element-1607">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1608">
                Content
              </label>
              <textarea value={editingSection.content} onChange={e => setEditingSection({
            ...editingSection,
            content: e.target.value
          })} rows={4} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1609"></textarea>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-id="element-1610">
              <div data-id="element-1611">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1612">
                  Order
                </label>
                <input type="number" value={editingSection.order} onChange={e => setEditingSection({
              ...editingSection,
              order: parseInt(e.target.value) || 0
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1613" />
              </div>
              <div data-id="element-1614">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1615">
                  Category
                </label>
                <select value={editingSection.category} onChange={e => setEditingSection({
              ...editingSection,
              category: e.target.value as 'usage' | 'accounts' | 'intellectual' | 'termination'
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1616">
                  <option value="usage" data-id="element-1617">Platform Usage</option>
                  <option value="accounts" data-id="element-1618">User Accounts</option>
                  <option value="intellectual" data-id="element-1619">Intellectual Property</option>
                  <option value="termination" data-id="element-1620">Service Termination</option>
                </select>
              </div>
              <div data-id="element-1621">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1622">
                  Language
                </label>
                <select value={editingSection.language || 'en'} onChange={e => setEditingSection({
              ...editingSection,
              language: e.target.value as 'en' | 'ar'
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1623">
                  <option value="en" data-id="element-1624">English</option>
                  <option value="ar" data-id="element-1625">Arabic</option>
                </select>
              </div>
              <div data-id="element-1626">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1627">
                  Status
                </label>
                <select value={editingSection.status} onChange={e => setEditingSection({
              ...editingSection,
              status: e.target.value as 'published' | 'draft'
            })} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1628">
                  <option value="draft" data-id="element-1629">Draft</option>
                  <option value="published" data-id="element-1630">Published</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2" data-id="element-1631">
              <button onClick={() => setEditingSection(null)} className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1632">
                Cancel
              </button>
              <button onClick={handleUpdateSection} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2" data-id="element-1633">
                <Save className="w-4 h-4" data-id="element-1634" />
                Update Section
              </button>
            </div>
          </div>
        </Card>}
      <Card className="p-5" data-id="element-1635">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4" data-id="element-1636">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1637">
            Preview ({language === 'en' ? 'English' : 'Arabic'})
          </h2>
          <Badge variant="secondary" size="sm" data-id="element-1638">
            Last Updated: {new Date().toLocaleDateString()}
          </Badge>
        </div>
        <div className="space-y-6" data-id="element-1639">
          {sections.filter(section => section.status === 'published' && section.language === language).sort((a, b) => a.order - b.order).map(section => <div key={section.id} className="space-y-2" data-id="element-1640">
                <div className="flex items-center gap-2" data-id="element-1641">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800" data-id="element-1642">
                    {getCategoryIcon(section.category)}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1643">
                    {section.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 ml-8" data-id="element-1644">
                  {section.content}
                </p>
              </div>)}
        </div>
      </Card>
    </div>;
};